import React, { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as d3 from "d3";
// import { useCookies } from "react-cookie";
import WorkflowContext from "../workflowContext";
import fetchOptionsBuilder from "../fetchOptionBuilder";
import { InputText } from "primereact/inputtext";
import { Slider } from "primereact/slider";
import CategoryFilterMenu from "../components/CategoryFilterMenu";
// import { Checkbox } from "primereact/checkbox";
import '../styles/pages/similarity.scss'


function getSessionStorageOrDefault(key, defaultValue) {
    const stored =  sessionStorage.getItem(key);
    if (!stored) {
      return defaultValue;
    }
    return JSON.parse(stored);

  }

const Similarity = () => {
    // const [cookies, setCookie, removeCookie] = useCookies(['auth', 'userid']);
    const workflow = useContext(WorkflowContext);
    const [maxLinkValue, setMaxLinkValue] = useState(0);
    const [minLinkValue, setMinLinkValue] = useState(0);
    const [linkValueCutoff, setLinkValueCutoff] = useState(0);
    const [propOptions, setPropOptions] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState('');
    const [textFilter, setTextFilter] = useState(/.+/g);
    const [propertyMap, setPropertyMap] = useState(null);
    const [shownGenes, setShownGenes] = useState(null);
    const [metadata, setMetadata] = useState({});
    const [strains, setStrains] = useState(null);
    // const [dataFailed, setDataFailed] = useState(false);
    const [zoomTransform, setZoomTransform] = useState(d3.zoomIdentity);
    const [prelimData, setPrelimData] = useState({
        links: [],
        nodes: []
    })
    const navigate = useNavigate();

    const strainMap = useMemo(() => {
        if (!strains) return null;
        const map = {};
        strains.forEach(strain => {
            map[strain.strain] = strain;
        });
        return map;
    }, [strains]);

    const data = useMemo(() => {
        if (!strainMap || !prelimData) return null;
        return {
            links: prelimData.links,
            nodes: prelimData.nodes.map(node => {
                return {...node, name: strainMap[node.id].name}
            })
        }
    }, [strainMap, prelimData])

    const colorScheme = [];
    if (selectedProperty) {
        const propLength = Object.keys(propertyMap[selectedProperty]).length;
        for (let i = 0; i < propLength; i++) {
            colorScheme.push(d3.interpolateRainbow(i / propLength));
        }
    }
    const color = d3.scaleOrdinal(colorScheme);

    const [apitoken, setApitoken] = useState(             // eslint-disable-line no-unused-vars
        getSessionStorageOrDefault('apitoken', null)
    );

    const getDataUserId = JSON.parse(sessionStorage.getItem('userid'));    

    useEffect(() => {
        document.title = `Similarity | Palmona Pathogenomics`
    }, []);

    console.log('data', data);

    // load the data
    useEffect(() => {
        fetch(process.env.REACT_APP_VISUALIZATION_API_PATH + "epinetwork", fetchOptionsBuilder(apitoken, workflow, getDataUserId))
            .then(res => res.json())
            .then(obj => {
                setPrelimData(obj);
                let [minVal, maxVal] = [Infinity, 0];
                obj.links.forEach(link => {
                    maxVal = Math.max(link.value, maxVal);
                    if (link.value > 0) {
                        minVal = Math.min(link.value, minVal);
                    }
                })
                setMaxLinkValue(maxVal);
                setMinLinkValue(minVal);
                setLinkValueCutoff(minVal + ((maxVal-minVal) / 2));
            })
            .catch(err => {
                console.error(err);
                // setDataFailed(true);
            })
        fetch(process.env.REACT_APP_VISUALIZATION_API_PATH + `straindata/property_list`, fetchOptionsBuilder(apitoken, workflow, getDataUserId))
            .then(res => res.json())
            .then(cats => setPropOptions(cats))
            .catch(err => {
                console.error(err);
                // setDataFailed(true);
            })
        fetch(process.env.REACT_APP_VISUALIZATION_API_PATH + 'metadata/json', fetchOptionsBuilder(apitoken, workflow, getDataUserId))
            .then(res => res.json())
            .then(val => setMetadata(val))
            .catch(err => {
                console.error(err);
            })
        fetch(process.env.REACT_APP_VISUALIZATION_API_PATH + 'strains/json', fetchOptionsBuilder(apitoken, workflow, getDataUserId))
            .then(res => res.json())
            .then(val => setStrains(val))
            .catch(err => console.error(err))
    }, [workflow, apitoken, getDataUserId]);

    // create category map
    useEffect(() => {
        if (data && data.nodes.length && propOptions.length) {
            setSelectedProperty(propOptions[0]);
            const newPropertyMap = {};
            propOptions.forEach(property => {
                newPropertyMap[property] = {};
            });
            data.nodes.forEach(node => {
                for (const property in newPropertyMap) {
                    if (!newPropertyMap[property][node[property]]) newPropertyMap[property][node[property]] = 0;
                    newPropertyMap[property][node[property]]++;
                }
            });
            setPropertyMap(newPropertyMap);
        }
    }, [data, propOptions])

    // draw the svg
    useEffect(() => {
        d3.select("#similarity-graph svg").remove();
        d3.select("#similarity-graph div").remove();

        if (!data || !data.nodes.length || !selectedProperty) return;

        const width = window.innerWidth;
        const height = window.innerHeight + 100;
      
        // Remove filtered out data nodes and links
        const nodeSet = new Set();
        const filterNodes = node => {
            if (!node.name.match(textFilter)) return false;
            if (shownGenes && !shownGenes.has(node.id)) return false;
            nodeSet.add(node.id);
            return true;
        }

        const filterLinks = link => {
            if (link.value < linkValueCutoff) return false;
            if (!nodeSet.has(link.source) || !nodeSet.has(link.target)) return false;
            return true;
        }


        // The force simulation mutates links and nodes, so create a copy
        // so that re-evaluating this cell produces the same result.
        const nodes = data.nodes.filter(node => filterNodes(node)).map(d => ({...d}));
        const links = data.links.filter(link => filterLinks(link)).map(d => ({...d}));
      
        // Create a simulation with several forces.
        const simulation = d3.forceSimulation(nodes)
            .force("link", d3.forceLink(links).id(d => d.id))
            .force("charge", d3.forceManyBody())
            .force("x", d3.forceX())
            .force("y", d3.forceY());
      
        // Create the SVG container.
        const svg = d3.create("svg")
            .attr("width", width)
            .attr("height", height)
            .attr("viewBox", [-width / 2, -height / 2, width, height])
            .attr("style", "max-width: 100%; height: auto;");
      
        // Add a line for each link, and a circle for each node.
        const breakpoint = (maxLinkValue - linkValueCutoff) / 5;
        const link = svg.append("g")
            .attr("stroke", "#999")
            .attr("stroke-opacity", 0.6)
            .selectAll("line")
            .data(links)
            .join("line")
            .attr("stroke-width", d => (d.value - linkValueCutoff) / breakpoint);
      
        const node = svg.append("g")
            .selectAll("circle")
            .data(nodes)
            .join("circle")
            .attr("r", 5)
            .attr("fill", d => color(d[selectedProperty]))
      
        node.attr('id', d => 'id' + d.id)
        link.attr('id', d => 'link' + d.index)
        node.append("title")
            .text(d => d.id);
      
        // Add a drag behavior.
        node.call(d3.drag()
              .on("start", dragstarted)
              .on("drag", dragged)
              .on("end", dragended));
        
        // Set the position attributes of links and nodes each time the simulation ticks.
        simulation.on("tick", () => {
          link
              .attr("x1", d => d.source.x)
              .attr("y1", d => d.source.y)
              .attr("x2", d => d.target.x)
              .attr("y2", d => d.target.y);
      
          node
              .attr("cx", d => d.x)
              .attr("cy", d => d.y);
        });
      
        // Reheat the simulation when drag starts, and fix the subject position.
        function dragstarted(event) {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          event.subject.fx = event.subject.x;
          event.subject.fy = event.subject.y;
        }
      
        // Update the subject (dragged node) position during drag.
        function dragged(event) {
          event.subject.fx = event.x;
          event.subject.fy = event.y;
        }
      
        // Restore the target alpha so the simulation cools after dragging ends.
        // Unfix the subject position now that it’s no longer being dragged.
        function dragended(event) {
          if (!event.active) simulation.alphaTarget(0);
          event.subject.fx = null;
          event.subject.fy = null;
        }
      
        // When this cell is re-run, stop the previous simulation. (This doesn’t
        // really matter since the target alpha is zero and the simulation will
        // stop naturally, but it’s a good practice.)
        // invalidation.then(() => simulation.stop());
      
        document.getElementById('similarity-graph').append(svg.node())

        const tooltip = d3.select('#similarity-graph')
            .append('div')
            .style("opacity", 0)
            .attr("class", "tooltip")
            .style("backgroud-color", "white")
            .style("border", "solid")
            .style("padding", "5px")
            .style("width", "100px")
            .style("z-index", "10")
            .style("background-color", "white")
            .style("font-size", "12px")
            .style("position", "absolute")

        const mouseoverNode = e => {
            const nodeObj = e.target.__data__;
            let tooltipText = `<b>Genome Name: ${nodeObj.name}</b><br>`;
            tooltipText += `<b>${selectedProperty}: ${nodeObj[selectedProperty]}</b><br>`
            for (const property in nodeObj) {
                if (propOptions.includes(property) && property !== selectedProperty) {
                    tooltipText += `${property}: ${nodeObj[property]}<br>`
                }
            }
            d3.select(`#id${nodeObj.id}`)
                .attr('r', 10)
            tooltip
                .style("opacity", 1)
                .style("left", (e.pageX - 250) + "px")
                .style("top", (e.pageY - 50) + "px")
                .html(tooltipText)
        }

        const mouseoutNode = e => {
            const nodeObj = e.target.__data__;
            d3.select(`#id${nodeObj.id}`)
                .attr('r', 5)
            tooltip
                .style("opacity", 0)
                .html('')
        }

        const mouseoverLink = e => {
            const linkObj = e.target.__data__;
            let tooltipText = `
                Source: ${linkObj.source.id}<br>
                Target: ${linkObj.target.id}<br>
                Similarity: ${linkObj.value}`
            d3.select(`#link${linkObj.index}`)
                .attr('stroke', '#ff0000')
            tooltip
                .style("opacity", 1)
                .style("left", (e.pageX - 250) + "px")
                .style("top", (e.pageY - 50) + "px")
                .html(tooltipText)
        }

        const mouseoutLink = e => {
            const linkObj = e.target.__data__;
            d3.select(`#link${linkObj.index}`)
                .attr('stroke', '#999');
            tooltip
                .style("opacity", 0)
                .html('')
        }

        const clickNode = e => {
            navigate(`/strains/detail/${e.target.__data__.id}`);
        }

        node
            .on("mouseover", e => mouseoverNode(e))
            .on("mouseout", e => mouseoutNode(e))
            .on("click", e => clickNode(e))
        link
            .on("mouseover", e => mouseoverLink(e))
            .on('mouseout', e => mouseoutLink(e))

        // add zoom behavior
        const handleZoom = e => {
            setZoomTransform(e.transform);
            d3.selectAll("g")
                .attr('transform', e.transform);
        }

        let zoom = d3.zoom()
	        .translateExtent([[-width, -height], [width, height]])
            .on('zoom', handleZoom);

        svg.call(zoom);
        // apply initial zoom or stateful transform value on reload
        if (zoomTransform.x === 0 && zoomTransform.y === 0 && zoomTransform.k === 1) {
            const initialZoom = 1/Math.sqrt(data.nodes.length) * 20;
            setZoomTransform(d3.zoomIdentity.scale(initialZoom));
            svg.call(zoom.transform, d3.zoomIdentity.scale(initialZoom));
        } else {
            svg.call(zoom.transform, zoomTransform);
        }
        

    }, [data, linkValueCutoff, maxLinkValue, textFilter, selectedProperty, metadata, color, navigate, propOptions, shownGenes, zoomTransform]);

    return (
        <div id='similarity-container'>
            <div id='side-display'>
                <h3>EpiNetwork Graph</h3>
                <div className="w-14rem">
                    <CategoryFilterMenu 
                        propOptions = {propOptions} 
                        selectedProperty={selectedProperty} 
                        setSelectedProperty={setSelectedProperty} 
                        setTextFilter={setTextFilter}
                        metadata={metadata}
                        shownGenes={shownGenes}
                        setShownGenes={setShownGenes}
                    />
                    <h5 id="cutoff-input-label">Similarity Coefficient Lower Bound</h5>
                    <InputText id="cutoff-input" keyfilter="num" value={linkValueCutoff} onChange={(e) => setLinkValueCutoff(Number(e.target.value))} className="w-full"/>
                    <Slider
                        value={linkValueCutoff} 
                        onChange={(e) => setLinkValueCutoff(Math.round(e.value * 100) / 100)} 
                        className="w-full" 
                        max={maxLinkValue}
                        min={minLinkValue} 
                        step={(maxLinkValue - minLinkValue)/100}
                    />
                </div>
            </div>
            <div id="similarity-graph"></div>
            <div style={{marginTop:"2rem"}} className="flex-item menu" id="category-filter-menu">
                {propertyMap &&
                    <div>
                    <span style={{ fontWeight: "bold", marginBottom: "1rem" }}>{selectedProperty}</span>
                    {Object.entries(propertyMap[selectedProperty]).map(([value, count]) => (
                        <div>
                        <svg width="20" height="20">
                            <circle cx="10" cy="10" r="8" fill={color(value)}></circle>
                        </svg>
                        <span style={{ color: color(value) }}>{value}</span>
                        </div>
                    ))}
                    </div>
              }
          </div>
        </div>
    )
}

export default Similarity;